import { useContext, useEffect } from 'react';

import { EditorContent } from '@tiptap/react';

import { ActiveEditorContext } from '../ActiveEditorContext';
import type { TextEditorProps } from '../types';

import { useTextEditorForTableBlock } from '../hooks/useTextEditorForTableBlock.ts';
import { useTiptapUndoRedoCommands } from '../hooks/useUndoRedoCommands';
import { DEFAULT_CURRENCY_CODE } from '../../../grid/reduxStore/documentSettingsSlice.ts';
import { isNumericColumn } from '../../Block/Table/helper';

const TextEditorForTableBlock = ({
  placeholder,
  onChange,
  inputClassName,
  isTemplate,
  value,
  columnType,
  rootClassName,
  userPathname,
  uploadFileOptions,
  isPreview = false,
  editable = true,
  variables,
  documentCurrencyCode = DEFAULT_CURRENCY_CODE,
  hasFocus,
  ...editorOptions
}: TextEditorProps) => {
  const { setActiveEditor } = useContext(ActiveEditorContext);

  const editor = useTextEditorForTableBlock({
    placeholder,
    onChange,
    value,
    tab: isPreview ? 'preview' : 'editor',
    editable,
    userPathname,
    isTemplate,
    uploadFileOptions,
    parseOptions: {
      preserveWhitespace: 'full',
    },
    variables,
    documentCurrencyCode,
    ...editorOptions,
  });

  useEffect(() => {
    if (editor && editor.getHTML() !== value) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  useTiptapUndoRedoCommands(editor);
  useEffect(() => {
    const shouldLoadEditor = hasFocus && editable && editor && (!columnType || !isNumericColumn(columnType)) && !isPreview;
    if (shouldLoadEditor) {
      setActiveEditor(editor);
    }
  }, [editor, setActiveEditor, editable, columnType, isPreview, hasFocus]);

  if (!editable) {
    return <EditorContent editor={editor} className={inputClassName} />;
  }

  return (
    <div className={rootClassName}>
      <EditorContent editor={editor} className={inputClassName} />
    </div>
  );
};

export default TextEditorForTableBlock;
